import { BitkitDrawer, BitkitLink, BitkitLinkButton, BitkitSpinner, IconStep, rem } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Portal } from '@chakra-ui/react/portal';
import { Table } from '@chakra-ui/react/table';
import { Text } from '@chakra-ui/react/text';
import { useContext } from 'react';

import useInvocationStepExecution from '@/components/BuildCache/hooks/useInvocationStepExecution';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { HEADER_HEIGHT } from '@/constants';
import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';
import { getBuildCacheCiProviderName } from '@/models/CiProvider';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import { useInvocationListTableContext } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationListTableContext';
import ChildInvocationsSection from '@/pages/BuildCache/InvocationDetailsPage/ChildInvocationsSection';
import { formatDateTime } from '@/utils/commonFormats';
import { buildDetailsPagePath, buildToolInvocationPath, workflowEditorPath } from '@/utils/monolithApiRouteService';

type InvocationDetailsDrawerProps = {
  invocation: BuildToolInvocationWithDetails;
  isOpen: boolean;
  onClose: () => void;
};

const InvocationDetailsDrawer = ({ invocation, isOpen, onClose }: InvocationDetailsDrawerProps) => {
  const {
    account: { slug: workspaceSlug },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const { isBuildSpecific, isProjectSpecific } = useInvocationListTableContext();
  const hideProjectTitle = isBuildSpecific || isProjectSpecific;

  const {
    invocationId,
    ciProvider,
    ciRunUrl,
    commitHash,
    externalBuildId,
    externalWorkflowName,
    hasAnyChildInvocation,
    workflowName,
    projectSlug,
    buildSlug,
    tool,
    stepExecutionId,
    username,
    wrapper,
  } = invocation;

  const {
    stepTitle,
    error: stepFetchError,
    isLoading: isLoadingStepData,
    refetch: refetchStepData,
  } = useInvocationStepExecution(workspaceSlug, tool, invocationId, stepExecutionId, !!stepExecutionId && isOpen);

  return (
    <BitkitDrawer.Root
      variant="floating"
      size="md"
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      trapFocus={false}
      modal={false}
      preventScroll={false}
      closeOnInteractOutside={false}
    >
      <Portal>
        <BitkitDrawer.Backdrop onClick={onClose} background="utilities/overlay-side" />
        <BitkitDrawer.Positioner
          insetBlockStart={{ base: 0, tablet: HEADER_HEIGHT + 16 }}
          paddingBlockStart="0"
          paddingBlockEnd={{ base: '0', tablet: '16' }}
          paddingInlineEnd={{ base: '0', tablet: '16' }}
          paddingInlineStart="0"
        >
          <BitkitDrawer.Content
            data-clarity-unmask="true"
            borderRadius={{ base: '0', tablet: '12' }}
            borderWidth="1"
            borderStyle="solid"
            borderColor="border/regular"
            borderInlineWidth={{ base: '0', tablet: '1' }}
            borderBlockEndWidth={{ base: '0', tablet: '1' }}
            _open={{
              animationName: {
                base: 'slide-from-bottom-full, fade-in',
                tablet: 'slide-from-right-full, fade-in',
              },
            }}
            _closed={{
              animationName: {
                base: 'slide-to-bottom-full, fade-out',
                tablet: 'slide-to-right-full, fade-out',
              },
            }}
          >
            <BitkitDrawer.CloseButton />
            <BitkitDrawer.Header>
              <Box display="flex" flexDirection="column" paddingInlineEnd={rem(56)}>
                <Text as="h3" textStyle="heading/h3" color="text/primary" truncate>
                  {invocation.shortCommand || invocation.command}
                </Text>
                <Box display="flex" flexDirection="row" alignItems="center">
                  {!hideProjectTitle && invocation.projectTitle && (
                    <Text
                      as="span"
                      textStyle="body/sm/regular"
                      color="text/secondary"
                      _after={{
                        content: `'•'`,
                        color: 'icon/tertiary',
                        marginInline: '6',
                      }}
                      truncate
                    >
                      {invocation.projectTitle}
                    </Text>
                  )}
                  <Text flexShrink="0" textStyle="body/sm/regular" color="text/secondary">
                    {formatDateTime(invocation.startedAt, {
                      hideSeconds: true,
                      hideYear: true,
                    })}
                  </Text>
                  {projectSlug && buildSlug && (
                    <BitkitLink
                      flexShrink="0"
                      colorVariant="purple"
                      href={buildDetailsPagePath(projectSlug, buildSlug)}
                      isExternal
                      textStyle="body/sm/regular"
                      _before={{
                        content: `'•'`,
                        color: 'icon/tertiary',
                        display: 'inline-block',
                        marginInline: '6',
                        textDecoration: 'none',
                      }}
                    >
                      View build
                    </BitkitLink>
                  )}
                </Box>
              </Box>
            </BitkitDrawer.Header>
            <BitkitDrawer.Body>
              <Box display="flex" flexDirection="column" gap="32">
                <Table.ScrollArea overflowX="hidden">
                  <Table.Root
                    tableLayout="fixed"
                    variant="card"
                    size="sm"
                    rowHover={false}
                    borderColor="border/minimal"
                    borderRadius="4"
                    borderStyle="solid"
                    borderWidth="1"
                  >
                    <colgroup>
                      <Box as="col" width={rem(200)} />
                      <Box as="col" />
                    </colgroup>
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell textStyle="body/md/semibold" color="text/primary">
                          Invocation ID
                        </Table.Cell>
                        <Table.Cell>
                          <BitkitLink
                            title={invocationId}
                            href={buildToolInvocationPath(workspaceSlug, invocationId, tool)}
                            colorVariant="purple"
                            textStyle="body/md/regular"
                          >
                            <Text truncate>{invocationId}</Text>
                          </BitkitLink>
                        </Table.Cell>
                      </Table.Row>
                      {commitHash && (
                        <Table.Row>
                          <Table.Cell textStyle="body/md/semibold" color="text/primary">
                            Commit hash
                          </Table.Cell>
                          <Table.Cell title={commitHash} textStyle="body/md/regular" color="text/body">
                            {commitHash?.slice(0, 7)}
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {ciProvider && (
                        <Table.Row>
                          <Table.Cell textStyle="body/md/semibold" color="text/primary">
                            CI provider
                          </Table.Cell>
                          <Table.Cell
                            title={getBuildCacheCiProviderName(ciProvider)}
                            textStyle="body/md/regular"
                            color="text/body"
                          >
                            {getBuildCacheCiProviderName(ciProvider)}
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {ciRunUrl && externalBuildId && (
                        <Table.Row>
                          <Table.Cell textStyle="body/md/semibold" color="text/primary">
                            CI run
                          </Table.Cell>
                          <Table.Cell title={externalBuildId}>
                            <BitkitLink href={ciRunUrl} isExternal colorVariant="purple" textStyle="body/md/regular">
                              <Text truncate>{`#${externalBuildId}`}</Text>
                            </BitkitLink>
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {externalWorkflowName && (
                        <Table.Row>
                          <Table.Cell textStyle="body/md/semibold" color="text/primary">
                            Job
                          </Table.Cell>
                          <Table.Cell title={externalWorkflowName}>
                            <Text textStyle="body/md/regular" color="text/body" truncate>
                              {externalWorkflowName}
                            </Text>
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {workflowName && (
                        <Table.Row>
                          <Table.Cell textStyle="body/md/semibold" color="text/primary">
                            Workflow
                          </Table.Cell>

                          <Table.Cell title={workflowName}>
                            {projectSlug ? (
                              <BitkitLink
                                href={workflowEditorPath(projectSlug, workflowName)}
                                isExternal
                                colorVariant="purple"
                              >
                                <Text truncate>{workflowName}</Text>
                              </BitkitLink>
                            ) : (
                              <Text textStyle="body/md/regular" color="text/body" truncate>
                                {workflowName}
                              </Text>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {stepExecutionId && (
                        <Table.Row>
                          <Table.Cell textStyle="body/md/semibold" color="text/primary">
                            Bitrise Step
                          </Table.Cell>
                          <Table.Cell>
                            {isLoadingStepData && (
                              <Box alignItems="center" display="flex" gap="4">
                                <BitkitSpinner color="text/tertiary" size="md" />
                                <Text textStyle="body/md/regular" color="text/tertiary">
                                  Loading Step...
                                </Text>
                              </Box>
                            )}
                            {stepFetchError && (
                              <Box alignItems="center" display="flex" gap="4">
                                <IconStep size="16" color="icon/tertiary" />
                                <Text textStyle="body/md/regular" color="text/tertiary">
                                  Failed to load Step (
                                  <BitkitLinkButton onClick={() => refetchStepData()}>Retry</BitkitLinkButton>)
                                </Text>
                              </Box>
                            )}
                            {stepTitle && (
                              <Text textStyle="body/md/regular" color="text/body" truncate>
                                {stepTitle}
                              </Text>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {username && (
                        <Table.Row>
                          <Table.Cell textStyle="body/md/semibold" color="text/primary">
                            Username
                          </Table.Cell>
                          <Table.Cell>
                            <Text textStyle="body/md/regular" color="text/body" truncate>
                              {username}
                            </Text>
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {wrapper && (
                        <Table.Row>
                          <Table.Cell textStyle="body/md/semibold" color="text/primary">
                            Wrapper
                          </Table.Cell>
                          <Table.Cell>
                            <Text textStyle="code/lg" color="text/body" truncate>
                              {wrapper}
                            </Text>
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table.Root>
                </Table.ScrollArea>
                {tool === BuildCacheToolIds.reactNative && hasAnyChildInvocation && (
                  <ChildInvocationsSection invocationId={invocationId} enabled={isOpen} showTitle={false} />
                )}
              </Box>
            </BitkitDrawer.Body>
          </BitkitDrawer.Content>
        </BitkitDrawer.Positioner>
      </Portal>
    </BitkitDrawer.Root>
  );
};

export default InvocationDetailsDrawer;
